import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaHandHoldingDollar } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import List from '../../components/Crud/List';
import { toMask } from '../../services/currency';
import CustomButton from '../../components/CustomButton/CustomButton';
import CustomModal from '../../components/CustomModal/CustomModal';
import {
  PayrollInvoiceWorkerViewModel,
  PayrollViewModel,
} from '../../models/payroll/PayrollViewModel';
import PayrollsFilter, { PayrollFilterFields } from './PayrollFilter';
import {
  getLastFilterByKey,
  upsertFilterToKey,
} from '../../services/filterService';

export default function PayrollsShow() {
  const pageKey = 'payrolls';
  const { payrollId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [payroll, setPayroll] = useState<null | PayrollViewModel>(null);
  const [invoiceWorkers, setInvoiceWorkers] = useState<
    PayrollInvoiceWorkerViewModel[]
  >([]);
  const [payrollsSelected, setPayrollsSelected] = useState<number[]>([]);
  const [payrollsProgress, setPayrollsProgress] = useState<number>(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isCheckAll, setIsCheckAll] = useState(false);

  const [filterFields, setFilterFields] = useState<PayrollFilterFields>(
    null as unknown as PayrollFilterFields
  );

  const {
    user: { organization },
  } = useAuth();

  async function getPayroll() {
    if (organization) {
      setIsLoading(true);
      const { data } = await api.get(
        `organizations/${organization.id}/payrolls/${payrollId}`
      );
      setPayroll(new PayrollViewModel(data));
      setIsLoading(false);
    }
  }

  async function getInvoiceWorkers() {
    if (organization && payroll) {
      setIsLoading(true);
      const filters = getLastFilterByKey(pageKey, PayrollFilterFields);
      setFilterFields(filters);

      const { data } = await api.get(
        `organizations/${organization.id}/payrolls/${payrollId}/workers`,
        { params: filters }
      );
      const workers = data.data.map(
        (d: any) => new PayrollInvoiceWorkerViewModel(d)
      );
      setInvoiceWorkers(workers);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setPayrollsSelected([]);
    setPayrollsProgress(0);
    getPayroll();
  }, [organization]);

  useEffect(() => {
    if (payroll) {
      getInvoiceWorkers();
    }
  }, [payroll]);

  function formatCell(
    top: JSX.Element | string,
    bottom: JSX.Element | string = ''
  ) {
    return (
      <div className='d-flex flex-column'>
        <div>{top}</div>
        {bottom ? (
          <div>
            <small className='text-muted'>{bottom}</small>
          </div>
        ) : undefined}
      </div>
    );
  }

  const setPaid = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Deseja continuar?')) {
      return;
    }

    await api.post(
      `organizations/${organization.id}/payrolls/${payrollId}/workers/${id}/pay`
    );
    getInvoiceWorkers();
  };

  const toggleAll = () => {
    setIsCheckAll(!isCheckAll);
    const payrolls = (invoiceWorkers || []).filter(
      (iw: PayrollInvoiceWorkerViewModel) => !iw.paidAt
    );
    payrolls.forEach((p) => p.toggleChecked());
    setPayrollsSelected(
      payrolls.map((p: PayrollInvoiceWorkerViewModel) => p.id)
    );
    if (isCheckAll) {
      setPayrollsSelected([]);
    }
  };

  const paidPayrollsSelected = async () => {
    setIsOpenModal(true);
    setPayrollsProgress(0);
    const totalItems = payrollsSelected.length;
    let totalSended = 0;
    /* eslint-disable no-restricted-syntax */
    for (const payrollSelected of payrollsSelected) {
      /* eslint-disable no-await-in-loop */
      await api.post(
        `organizations/${organization.id}/payrolls/${payrollId}/workers/${payrollSelected}/pay`
      );
      totalSended += 1;
      setPayrollsProgress(Math.round((totalSended / totalItems) * 100));
    }
    toast.success('Funcionários pagos com sucesso');
    setPayrollsSelected([]);
    setIsOpenModal(false);

    toggleAll();
    getInvoiceWorkers();
  };

  const onPayrollChecked = (target: EventTarget & HTMLInputElement): void => {
    const { id, checked } = target;
    setPayrollsSelected([...payrollsSelected, Number(id)]);
    if (!checked) {
      setPayrollsSelected(
        payrollsSelected.filter((item) => item !== Number(id))
      );
    }
  };

  const openPrintPage = (
    printPayrollId: number | undefined,
    workerId: number
  ) => {
    if (!printPayrollId) {
      return;
    }

    const publicUrl = process.env.REACT_APP_API_URL?.replace('api/', '');
    const printWindow = window.open(
      `${publicUrl}pdfs/payrolls/${printPayrollId}/workers/${workerId}`,
      '_blank',
      'width=800,height=600,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no'
    );

    if (printWindow) {
      printWindow.focus();
    } else {
      alert('Pop-up bloqueado! Por favor, habilite essa funcionalidade');
    }
  };

  const onFilterSubmit = (data: PayrollFilterFields) => {
    const filter = new PayrollFilterFields(data);
    upsertFilterToKey(pageKey, filter);
    getInvoiceWorkers();
  };

  return (
    <List
      isLoading={isLoading}
      title={`Folha ${payrollId}`}
      withNewAction={false}
      newItemUrl=''
      filter={
        <PayrollsFilter
          pageKey={pageKey}
          filterFields={filterFields}
          onSubmit={onFilterSubmit}
        />
      }
    >
      {/* TODO: <PayrollInvoiceList payroll={payroll} /> */}
      <table className='table table-hover'>
        <thead>
          <tr>
            <th scope='col'>
              <input
                className='form-check-input'
                type='checkbox'
                onClick={() => toggleAll()}
              />
            </th>
            <th scope='col'>OS</th>
            <th scope='col'>Cliente</th>
            <th scope='col'>Nat. Operação</th>
            <th scope='col' className='text-center'>
              Início
            </th>
            <th scope='col' className='text-center'>
              Fim
            </th>
            <th scope='col'>Funcionário</th>
            <th scope='col' className='text-end'>
              Bônus
            </th>
            <th scope='col' className='text-end'>
              Valor + Bônus
            </th>
            <th scope='col' className='text-center'>
              Pagamento
            </th>
          </tr>
        </thead>
        <tbody>
          {(invoiceWorkers || []).map((iw: PayrollInvoiceWorkerViewModel) => {
            const invoiceStartedAt = new Date(iw.invoiceStartedAt);
            const invoiceFinishedAt = new Date(iw.invoiceFinishedAt);
            const paidAt = iw.paidAt && new Date(iw.paidAt);
            const invoiceLink = (
              <Link target='_blank' to={`/invoices/${iw.invoiceId}/edit`}>
                {iw.invoiceNumber || '#'}
              </Link>
            );

            return (
              <tr key={iw.id}>
                <td>
                  {!paidAt && (
                    <input
                      id={String(iw.id)}
                      name={iw.invoiceNumber}
                      className='form-check-input payroll-checkbox'
                      type='checkbox'
                      onChange={(e) => onPayrollChecked(e.target)}
                      checked={payrollsSelected.includes(iw.id)}
                    />
                  )}
                </td>
                <td>{formatCell(invoiceLink)}</td>
                <td>{iw.customerName}</td>
                <td>{iw.transactionNature}</td>
                <td className='text-center'>
                  {formatCell(
                    invoiceStartedAt.toLocaleDateString(),
                    invoiceStartedAt.toLocaleTimeString()
                  )}
                </td>
                <td className='text-center'>
                  {formatCell(
                    invoiceFinishedAt.toLocaleDateString(),
                    invoiceFinishedAt.toLocaleTimeString()
                  )}
                </td>
                <td>
                  <button
                    type='button'
                    className='btn btn-link text-black'
                    onClick={() => openPrintPage(payroll?.id, iw.workerId)}
                  >
                    {iw.workerName}
                  </button>
                </td>
                <td className='text-end'>{toMask(iw.bonus)}</td>
                <td className='text-end'>{toMask(iw.value)}</td>
                <td className='text-center'>
                  {paidAt ? (
                    formatCell(
                      paidAt.toLocaleDateString(),
                      paidAt.toLocaleTimeString()
                    )
                  ) : (
                    <CustomButton
                      className='btn btn-warning'
                      type='button'
                      handleClick={() => setPaid(iw.id)}
                    >
                      Pagar <FaHandHoldingDollar />
                    </CustomButton>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {payrollsSelected.length > 0 && (
        <CustomButton
          className='btn btn-warning mb-2'
          type='button'
          handleClick={() => paidPayrollsSelected()}
        >
          Pagar Funcionários Selecionados <FaHandHoldingDollar />
        </CustomButton>
      )}

      <CustomModal
        modalTitle='Realizando pagamentos dos funcionários'
        isOpen={isOpenModal}
        setIsOpenModal={setIsOpenModal}
      >
        <div
          className='progress'
          role='progressbar'
          aria-label='Example with label'
          aria-valuenow={payrollsProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className='progress-bar'
            style={{ width: `${payrollsProgress}%` }}
          >
            {payrollsProgress}%
          </div>
        </div>
      </CustomModal>
    </List>
  );
}
